import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import RpConfirm from "@/components/RpConfirm";
import { Formik as FormikBase } from "formik";
import { isEmpty, isEqual, sortBy } from "lodash-es";
import { Icon, Typography } from "@mui/material";
import { RpTab, RpTabs } from "@/components/muiCustom/tabs/JoinCampaignTab";
import JoinCampaignPageSubHeaderBase from "@/features/campaigns/components/joinCampaigns/JoinCampaignPageSubHeader";
import SubscribedLocationsBase from "./subscribedLocations/SubscribedLocations";
import RpSnackbarBase from "@/components/alerts/RpSnackbar";
import InActiveLocationsBase from "./inActiveLocations/InActiveLocations";
import { joinCampaignHelper } from "@/features/campaigns/shared/helpers/joinCampaignHelper";
import { RpConfirmDialog } from "@/helpers/utils";
import LocationsExpandCollapseButtons from "@/features/campaigns/components/LocationsExpandCollapseButtons";
import { CAMPAIGN_CONST } from "@/config/constants";
import RpSvgIconBase from "@/components/icons/RpSvgIcon";
import PreviewOnlyDialogBase from "@/features/campaigns/components/joinCampaignCommon/PreviewOnlyDialog";
import useIsParticipant from "@/features/auth/hooks/useIsParticipant";
import { shouldBlockUnsafeSubmission } from "@/helpers/sanitizeHelper";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";
import {
  buildBulkBudgetRecommendationRequest,
  buildBudgetRecommendationRequest,
  isBudgetRecommendationEnabled,
} from "@/features/campaigns/shared/helpers/budgetRecommendationHelper";

type LocationId = string | number;

type CampaignLocation = {
  id?: LocationId;
  [key: string]: unknown;
};

type CampaignData = {
  id?: LocationId;
  campaign_name?: string;
  campaign_type?: string;
  duration?: unknown;
  start_date?: unknown;
  end_date?: unknown;
  budget_type?: string;
  local_selection?: boolean;
  campaign_advanced_setting?: {
    is_required?: boolean;
  };
  locations: {
    subscribed?: CampaignLocation[];
    fallOutSide?: CampaignLocation[];
    unSubscribed?: CampaignLocation[];
  };
  [key: string]: unknown;
};

type LoggedInUser = {
  id?: LocationId;
  email?: string;
  first_name?: string;
  last_name?: string;
  participantData?: {
    id?: LocationId;
  };
};

type SelectedBrand = {
  id?: LocationId;
  [key: string]: unknown;
};

type JoinCampaignFormProps = {
  campaign: CampaignData;
  initialValues?: Record<string, unknown>;
  loggedInUser?: LoggedInUser;
  selectedBrand?: SelectedBrand;
  submit: (_data: Record<string, unknown>) => void;
  setIsDraft: (_isDraft: boolean) => void;
  productList?: unknown;
  getAdvanceSettingData?: unknown;
  isDraft?: boolean;
};

type FormErrorState = {
  id?: number;
  message?: string;
};

type AdvancedSettingErrors = Record<string, unknown>;

type RenderPageProps = {
  values: Record<string, unknown>;
  handleSubmit: React.FormEventHandler<HTMLFormElement>;
  setFieldValue: (..._args: unknown[]) => void;
  setFieldError: (..._args: unknown[]) => void;
  isSubmitting: boolean;
};

const shouldIgnoreBudgetRecommendationExplanation = (
  path: Array<string | number> = []
) => {
  const lastKey = path[path.length - 1];
  const parentKey = path[path.length - 2];
  const grandparentKey = path[path.length - 3];

  return (
    lastKey === "explanation" &&
    (parentKey === "budgetRecommendation" ||
      grandparentKey === "budgetRecommendationsByKey")
  );
};

const Formik = FormikBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const JoinCampaignPageSubHeader =
  JoinCampaignPageSubHeaderBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const SubscribedLocations =
  SubscribedLocationsBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const InActiveLocations =
  InActiveLocationsBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const PreviewOnlyDialog =
  PreviewOnlyDialogBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const RpSnackbar = RpSnackbarBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const RpSvgIcon = RpSvgIconBase as unknown as React.ElementType;

function TabPanel(props: {
  children?: React.ReactNode;
  value: number;
  index: number;
  [key: string]: unknown;
}) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`campaigns-tabpanel-${index}`}
      aria-labelledby={`campaigns-tab-${index}`}
      {...other}
      className="rp-campaign-tab"
    >
      {children}
    </div>
  );
}

export default function JoinCampaignForm({
  campaign,
  initialValues,
  loggedInUser,
  selectedBrand,
  submit,
  setIsDraft,
  productList,
  getAdvanceSettingData,
  isDraft,
}: JoinCampaignFormProps) {
  const isPreview = !useIsParticipant();
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [formError, setFormError] = useState<FormErrorState>({});
  const formErrorIdRef = useRef(0);
  const [fillAdvancedSetting, setFillAdvancedSetting] = useState(false);
  const [advancedSettingFormError, setAdvancedSettingFormError] =
    useState<AdvancedSettingErrors>({});
  const [expanded, setExpanded] = useState<LocationId[]>([]);
  const [
    isBudgetRecommendationInProgress,
    setIsBudgetRecommendationInProgress,
  ] = useState(false);
  const budgetRecommendationEnabled = useMemo(
    () => isBudgetRecommendationEnabled({ campaign, selectedBrand }),
    [campaign, selectedBrand]
  );

  useEffect(() => {
    if (!fillAdvancedSetting) {
      setAdvancedSettingFormError({});
    }
  }, [fillAdvancedSetting]);

  const isAdvancedSettingsFormValid = !Object.keys(advancedSettingFormError)
    ?.length;

  const setSubmitFormError = useCallback((error) => {
    formErrorIdRef.current += 1;
    setFormError({ ...error, id: formErrorIdRef.current });
  }, []);

  const validLocations = useMemo(() => {
    const {
      locations: { subscribed },
    } = campaign;
    return subscribed && subscribed.length ? sortBy(subscribed, "id") : [];
  }, [campaign]);

  const inValidLocationsCount = useMemo(() => {
    const {
      locations: { fallOutSide, unSubscribed },
    } = campaign;
    return fallOutSide.length + unSubscribed.length;
  }, [campaign]);

  const allLocationsIds = useMemo(() => {
    const {
      locations: { fallOutSide, unSubscribed },
    } = campaign;

    if (selectedTab === 0) {
      return validLocations && validLocations.length
        ? validLocations.map((l) => l.id)
        : [];
    } else if (selectedTab === 1) {
      const fos =
        fallOutSide && fallOutSide.length ? fallOutSide.map((l) => l.id) : [];
      const us =
        unSubscribed && unSubscribed.length
          ? unSubscribed.map((l) => l.id)
          : [];
      return [...fos, ...us];
    }
  }, [campaign, selectedTab, validLocations]);

  useEffect(() => {
    if (validLocations.length === 0 && inValidLocationsCount > 0) {
      setSelectedTab(1);
    }
  }, [inValidLocationsCount, validLocations]);

  useEffect(() => {
    if (allLocationsIds.length) {
      if (allLocationsIds.length <= 5) {
        setExpanded([...allLocationsIds]);
      } else {
        setExpanded([allLocationsIds[0]]);
      }
    } else {
      setExpanded([]);
    }
  }, [allLocationsIds]);

  const handleOnChangeAdvancedSettingError = useCallback(
    ({
      locId,
      errors,
    }: {
      locId: LocationId;
      errors?: Record<string, unknown>;
    }) => {
      const hasErr = errors && Object.keys(errors)?.length;
      if (hasErr) {
        setAdvancedSettingFormError((p) => {
          if (isEqual(p[locId], errors)) {
            return p;
          }

          return { ...p, [locId]: errors };
        });
      } else {
        setAdvancedSettingFormError((p) => {
          if (!Object.prototype.hasOwnProperty.call(p, locId)) {
            return p;
          }

          // eslint-disable-next-line no-unused-vars
          const { [locId as any]: _, ...rest } = p;
          return { ...rest };
        });
      }
    },
    []
  );

  const handleGetBudgetRecommendation = useCallback(
    ({ loc, locFormData }) => {
      if (!budgetRecommendationEnabled) {
        return Promise.reject(
          new Error("Budget recommendation is not enabled for this brand.")
        );
      }

      return campaignService.getBudgetRecommendation(
        buildBudgetRecommendationRequest({
          campaign,
          selectedBrand,
          loc,
          locFormData,
        })
      );
    },
    [budgetRecommendationEnabled, campaign, selectedBrand]
  );

  const handleGetBulkBudgetRecommendation = useCallback(
    ({ locations, signal }) => {
      if (!budgetRecommendationEnabled) {
        return Promise.reject(
          new Error("Budget recommendation is not enabled for this brand.")
        );
      }

      return campaignService.getBulkBudgetRecommendation(
        buildBulkBudgetRecommendationRequest({
          campaign,
          selectedBrand,
          locations,
        }),
        { signal }
      );
    },
    [budgetRecommendationEnabled, campaign, selectedBrand]
  );

  const handleStreamBulkBudgetRecommendationProgress = useCallback(
    ({ jobId, signal, onMessage, onComplete }) => {
      return campaignService.streamBulkBudgetRecommendationProgress(jobId, {
        signal,
        onMessage,
        onComplete,
      });
    },
    []
  );

  const handleFormSubmit = (values: Record<string, unknown>, actions: any) => {
    if (isPreview) {
      actions.setSubmitting(false);
      setPreviewDialogOpen(true);
      return;
    }

    if (
      shouldBlockUnsafeSubmission(
        values,
        {},
        {
          shouldIgnorePath: shouldIgnoreBudgetRecommendationExplanation,
        }
      )
    ) {
      actions.setSubmitting(false);
      return;
    }

    setFormError({});

    const { campaign_advanced_setting } = campaign;
    const { is_required } = campaign_advanced_setting || {};

    const { locs, locIds } = joinCampaignHelper.getValidLocations(
      values,
      campaign
    );
    const totalEnrollingLocations = locIds.length;
    const locationsFilledData = locs;

    if (locIds.length === 0) {
      setSubmitFormError({
        message: `Please make your selections!`,
      });
      actions.setSubmitting(false);
      return;
    }

    const { campaign_type } = campaign;
    if (campaign_type === CAMPAIGN_CONST.TYPE.NORMAL) {
      if (
        (joinCampaignHelper.isNormalCampaignConstraintViolated as any)(
          campaign,
          locationsFilledData,
          setSubmitFormError,
          locIds
        )
      ) {
        actions.setSubmitting(false);
        return;
      }
    } else {
      if (
        (joinCampaignHelper.isEvergreenCampaignConstraintViolated as any)(
          campaign,
          locationsFilledData,
          setSubmitFormError,
          values
        )
      ) {
        actions.setSubmitting(false);
        return;
      }
    }

    const totalActiveLocations = validLocations.length;

    if (totalEnrollingLocations === 0) {
      setSubmitFormError({
        message: `Please make your selections!`,
      });
      actions.setSubmitting(false);
      return;
    }

    const locationData = joinCampaignHelper.getFormattedLocationData(
      values,
      campaign,
      selectedBrand
    );

    if (!isDraft && !fillAdvancedSetting && is_required) {
      actions.setSubmitting(false);
      setFillAdvancedSetting(true);
      return;
    }

    const saveDraftData: Record<string, unknown> = {
      brand_id: selectedBrand?.id,
      email_id: loggedInUser?.email,
      user_id: loggedInUser?.id,
      user_name: `${loggedInUser?.first_name} ${loggedInUser?.last_name}`,
      fk_participant_id: loggedInUser?.participantData?.id,
      fk_campaign_id: campaign.id,
      fk_campaign_name: campaign.campaign_name,
      campaign_type: campaign.campaign_type,
      duration: campaign.duration,
      start_date: campaign.start_date,
      end_date: campaign.end_date,
      locationData: JSON.stringify(locationData),
      payment_status: 0,
      total_budget: joinCampaignHelper.getTotal(locationsFilledData),
      is_draft: true,
      splitBudgetEqually: false,
      budget_type: campaign?.budget_type,
    };
    if (campaign.budget_type === "Pre-set" && campaign.local_selection) {
      saveDraftData.is_draft = false;
      saveDraftData.is_enrolled = 1;
      saveDraftData.is_creative_selected = 1;
    }

    if (totalActiveLocations === totalEnrollingLocations) {
      submit(saveDraftData);
      actions.setSubmitting(false);
    } else {
      (RpConfirmDialog as any)(
        <RpConfirm
          message={`You're enrolling ${totalEnrollingLocations} of ${totalActiveLocations} locations in this campaign. Are you sure you want to proceed?`}
        />,
        () => {
          submit(saveDraftData);
          actions.setSubmitting(false);
        },
        () => {
          setIsDraft(false);
          actions.setSubmitting(false);
          return;
        }
      );
    }
  };

  const renderPage = ({
    values,
    handleSubmit,
    setFieldValue,
    setFieldError,
    isSubmitting,
  }: RenderPageProps) => {
    return (
      <form onSubmit={handleSubmit}>
        <JoinCampaignPageSubHeader
          onSaveAsDraft={() => setIsDraft(true)}
          campaign_name={campaign?.campaign_name}
          isSubmitting={isSubmitting}
          isAdvancedSettingsFormValid={isAdvancedSettingsFormValid}
          disableSelectionSubmitActions={isBudgetRecommendationInProgress}
          selectedTab={selectedTab}
          allLocationsIds={allLocationsIds}
        />
        <div className="join-campaign-form mx-lg-5 mx-3">
          <div className="join-campaign-form-page">
            <div className="my-1 pt-2 row">
              <div className="col-xl-5 mb-4 mb-xl-0">
                <Typography component="h2" variant="h6">
                  <Icon
                    component={RpSvgIcon}
                    name="campaign"
                    color="primary"
                    className="mr-2"
                  />
                  {`${campaign?.campaign_name} Campaign Selections`}
                </Typography>
                <Typography variant="caption" className="mb-2">
                  Make selections to customize advertising for each of your
                  locations below.
                </Typography>
              </div>
              <div className="col-xl col-xl-7 d-flex justify-content-start justify-content-lx-end pl-xl-0">
                <div className="row flex-fill align-items-center">
                  <div className="col mb-3 mb-lg-0 d-flex justify-content-center justify-content-sm-start justify-content-xl-end px-xl-0">
                    <RpTabs
                      value={selectedTab}
                      onChange={(_event: unknown, newValue: number) =>
                        setSelectedTab(newValue)
                      }
                      variant="scrollable"
                      scrollButtons={false}
                    >
                      <RpTab
                        label={`Eligible Locations (${validLocations.length})`}
                        iconPosition="start"
                        icon={
                          <Icon fontSize="inherit" className="mr-1">
                            dashboard
                          </Icon>
                        }
                      />
                      <RpTab
                        label={`Not Eligible Locations (${inValidLocationsCount})`}
                        iconPosition="start"
                        icon={
                          <Icon fontSize="inherit" className="mr-1">
                            dashboard
                          </Icon>
                        }
                      />
                    </RpTabs>
                  </div>
                  <div className="col-lg-3 pl-0 d-flex justify-content-end">
                    <LocationsExpandCollapseButtons
                      setExpanded={setExpanded}
                      expanded={expanded as any}
                      allLocationsIds={allLocationsIds as any}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="locations my-3">
              <TabPanel value={selectedTab} index={0}>
                <SubscribedLocations
                  values={values}
                  subscribedLocations={validLocations}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  campaign={campaign}
                  isSubmitting={isSubmitting}
                  onSaveAsDraft={() => setIsDraft(true)}
                  productList={productList}
                  getAdvanceSettingData={getAdvanceSettingData}
                  fillAdvancedSetting={fillAdvancedSetting}
                  setFillAdvancedSetting={setFillAdvancedSetting}
                  isAdvancedSettingsFormValid={isAdvancedSettingsFormValid}
                  handleOnChangeAdvancedSettingError={
                    handleOnChangeAdvancedSettingError
                  }
                  expanded={expanded}
                  setExpanded={setExpanded}
                  onGetBudgetRecommendation={handleGetBudgetRecommendation}
                  onGetBulkBudgetRecommendation={
                    handleGetBulkBudgetRecommendation
                  }
                  onStreamBulkBudgetRecommendationProgress={
                    handleStreamBulkBudgetRecommendationProgress
                  }
                  isBudgetRecommendationEnabled={budgetRecommendationEnabled}
                  onBudgetRecommendationInProgressChange={
                    setIsBudgetRecommendationInProgress
                  }
                />
              </TabPanel>
              <TabPanel value={selectedTab} index={1}>
                <InActiveLocations
                  fallOutSideLocations={campaign?.locations?.fallOutSide}
                  unSubscribedLocations={campaign?.locations?.unSubscribed}
                  selectedBrand={selectedBrand}
                  campaign={campaign}
                  values={values}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  expanded={expanded}
                  setExpanded={setExpanded}
                />
              </TabPanel>
            </div>
          </div>
        </div>
      </form>
    );
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleFormSubmit}
      >
        {(props: RenderPageProps) => renderPage(props)}
      </Formik>

      {!isEmpty(formError) && selectedTab === 0 && (
        <RpSnackbar
          show
          message={formError.message}
          key={formError.id || formError.message}
        />
      )}
      <PreviewOnlyDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
      />
    </>
  );
}
